/**
 * THE PPI DESK'S SCREENS: the board, one request, a new one by phone, and
 * the inspector's own list. The rules are src/domain/ppidesk.ts and the
 * doors are server/routes/ppidesk.ts; this file only draws and asks.
 *
 * An inspector who opens the board gets their own list instead, and the
 * server would refuse them the board anyway: the screen is a convenience,
 * the server is the rule.
 */

import { api, db } from "../data/client.ts";
import { shrink } from "../data/photos.ts";
import { hasCamera, captureNativePhoto, pickNativePhotos } from "../data/native.ts";
import { esc, mount, money, since } from "../ui/dom.ts";
import { callButtonsHtml, wireCallButtons } from "../ui/callplay.ts";
import {
  STEPS, STEP_LABEL, ROLE_WORD, seesEverything, mayApproveReport, mayMove, ppiCode, vehicleOf,
  type Request, type Step, type InspectorView, type Role,
} from "../domain/ppidesk.ts";

async function authed(path: string, body?: unknown): Promise<Response> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return fetch(api(path), {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
    body: body ? JSON.stringify(body) : undefined,
  });
}


const phone = (p: string | null) => {
  const d = String(p ?? "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : esc(p ?? "");
};
const tel = (p: string | null) => p ? `<a href="tel:${esc(String(p).replace(/[^\d+]/g, ""))}">${phone(p)}</a>` : "<span class=\"quiet\">none</span>";
const at = (iso: string | null) => iso
  ? new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  : "";

/* ───────────────────────────── the board ───────────────────────────── */

type Person = { id: string; full_name: string; role: Role; phone: string | null; market?: string | null; state?: string | null };
type Board = { me: { id: string; role: Role }; requests: Request[]; people: Person[]; searched?: string | null };

/** What was last typed in the board's search box, so Back from a request lands on the same results. */
let lastSearch = "";

/* THE BOARD KEEPS ITSELF CURRENT (Eber, 2026-10-22: "when I'm on the phone,
   I'm not able to see the new jobs coming in ... I'm able to see it on the
   desktop"). It drew once and never again, and a phone keeps the tab open
   for days, so the board on his phone was whatever it was when he first
   looked. It now re-reads every 20 seconds while it is on screen, and at
   once when the phone comes back to the tab. It redraws only when
   something changed, and never while somebody is typing in the search. */
const BOARD_EVERY = 20_000;
let boardTimer: number | undefined;
let boardSeen = "";

function boardBusy(): boolean {
  const q = document.getElementById("ppi-q") as HTMLInputElement | null;
  if (q && (document.activeElement === q || q.value.trim() !== lastSearch)) return true;
  return document.visibilityState !== "visible";
}

async function boardQuietReload(): Promise<void> {
  if (!document.getElementById("ppi-board") || boardBusy()) return;
  let b: Board;
  try {
    const r = await authed(`/api/ppidesk/board${lastSearch ? `?q=${encodeURIComponent(lastSearch)}` : ""}`);
    if (!r.ok) return;   // a blip on a background refresh is not worth a red box
    b = await r.json() as Board;
  } catch { return; }
  // Navigated away, or somebody started typing, while the read was out.
  if (!document.getElementById("ppi-board") || boardBusy()) return;
  if (JSON.stringify(b) === boardSeen) return;
  const y = window.scrollY;
  drawBoard(b);
  window.scrollTo(0, y);
}

function startBoardLive(): void {
  if (boardTimer === undefined) {
    boardTimer = window.setInterval(() => {
      if (!document.getElementById("ppi-board")) { clearInterval(boardTimer); boardTimer = undefined; return; }
      void boardQuietReload();
    }, BOARD_EVERY);
  }
}

/* Coming back to the tab is the one moment somebody definitely wants the
   newest thing, so that does not wait for the next tick. Once, for the page. */
document.addEventListener("visibilitychange", () => { void boardQuietReload(); });
window.addEventListener("pageshow", () => { void boardQuietReload(); });

export async function ppiBoard(): Promise<void> {
  mount(`<div class="card"><p class="quiet">Loading the board…</p></div>`);
  const r = await authed(`/api/ppidesk/board${lastSearch ? `?q=${encodeURIComponent(lastSearch)}` : ""}`);
  if (r.status === 403) return void ppiMine();
  if (!r.ok) return void mount(`<div class="card"><p class="msg bad">The board did not load. Try again.</p></div>`);
  drawBoard(await r.json() as Board);
  startBoardLive();
}

function drawBoard(b: Board): void {
  boardSeen = JSON.stringify(b);
  /* WHO IS WORKING IT, on every card (Anthony, 2026-10-18: "the first
     person to work the lead tags it, then others see they are working it"). */
  const nameOf = new Map(b.people.map((p) => [p.id, p.full_name]));
  const worker = (q: Request) => q.dispatcher_id
    ? `<small class="ppi-who${q.dispatcher_id === b.me.id ? " me" : ""}">${q.dispatcher_id === b.me.id ? "You are working this"
      : `${esc(nameOf.get(q.dispatcher_id) ?? "Somebody")} is working this`}</small>` : "";

  const lanes = [...STEPS.filter((s) => s !== "closed"), "missed" as Step];
  const by = (s: Step) => b.requests.filter((q) => q.step === s);
  const card = (q: Request) => `<a class="ppi-card" href="#/ppi/${esc(q.id)}">
      <b>${esc(ppiCode(q.code))} · ${esc(vehicleOf(q))}</b>
      <small>${esc([q.seller_city, q.seller_state].filter(Boolean).join(", ") || "city unknown")}
        ${q.seller_kind === "dealer" ? " · dealer" : q.seller_kind === "private" ? " · private" : ""}</small>
      <small>${q.step === "new" ? (q.price_approved_at ? `${(q as { link_sent?: boolean }).link_sent === false ? "Priced, link not sent yet" : "Link sent"} · $${esc(q.price)}` : "Price needs approval")
        : q.scheduled_for ? esc(at(q.scheduled_for)) : esc(since(q.step_at))}</small>
      <small>${esc(q.buyer_name ?? "")}${q.buyer_phone ? ` · ${phone(q.buyer_phone)}` : ""}</small>
      ${worker(q)}
    </a>`;
  /* SEARCH, by name, phone or P-code, across every request ever made
     (Eber, 2026-10-18: "when I search for the customer to text them
     they're not in there"). */
  const search = `<form class="ppi-search" id="ppi-find" role="search">
      <input id="ppi-q" type="search" inputmode="search" placeholder="Search name, phone or P-code" value="${esc(lastSearch)}"
        aria-label="Search requests">
      <button class="btn" type="submit">Search</button>
      ${lastSearch ? `<button class="btn ghost" type="button" id="ppi-clear">Clear</button>` : ""}</form>`;
  const wire = () => {
    document.getElementById("ppi-find")?.addEventListener("submit", (e) => {
      e.preventDefault();
      lastSearch = ((document.getElementById("ppi-q") as HTMLInputElement | null)?.value ?? "").trim();
      void ppiBoard();
    });
    document.getElementById("ppi-clear")?.addEventListener("click", () => { lastSearch = ""; void ppiBoard(); });
  };
  if (b.searched) {
    mount(`<div class="page ppi" id="ppi-board">
      <header class="page-head ppi-head"><div><h1>PPI Board</h1>
        <p>${b.requests.length} found for “${esc(b.searched)}”, newest first.</p></div>
        <a class="btn primary" href="#/ppinew">New Request</a></header>
      ${search}
      <section class="card"><div class="pad">${b.requests.length
        ? `<div class="ppi-list">${b.requests.map((q) => card(q).replace("</b>", ` · ${esc(STEP_LABEL[q.step as Step] ?? q.step)}</b>`)).join("")}</div>`
        : `<p class="quiet">Nothing matches. Try the last four digits of the phone.</p>`}</div></section></div>`);
    return wire();
  }

  /* A lane with nothing in it is one line, not a card a screen tall: on a
     phone the old empty "Nothing here." boxes pushed the work below the
     fold (Anthony, 2026-10-14: "fix margins and ui"). */
  mount(`<div class="page ppi" id="ppi-board">
    <header class="page-head ppi-head"><div><h1>PPI Board</h1>
      <p>You are ${esc(ROLE_WORD[b.me.role] ?? b.me.role)}. Tap a request to work it.</p></div>
      <a class="btn primary" href="#/ppinew">New Request</a></header>
    ${search}
    <div class="ppi-lanes">${lanes.map((s) => `<section class="ppi-lane card${by(s).length ? "" : " none"}">
      <div class="card-head"><span>${esc(STEP_LABEL[s])}</span><span class="ppi-count">${by(s).length}</span></div>
      ${by(s).length ? `<div class="ppi-list">${by(s).map(card).join("")}</div>` : ""}</section>`).join("")}</div></div>`);
  wire();
}

/* ───────────────────────────── one request ───────────────────────────── */

type Ev = { at: string; kind: string; step_from: string | null; step_to: string | null; touch_key: string | null;
  audience: string | null; body: string | null; actor_name: string | null };

export async function ppiRequestPage(id: string): Promise<void> {
  mount(`<div class="card"><p class="quiet">Loading…</p></div>`);
  const [rq, rb] = await Promise.all([authed(`/api/ppidesk/request/${encodeURIComponent(id)}`), authed("/api/ppidesk/board")]);
  if (!rq.ok) return void mount(`<div class="card"><p class="msg bad">That request is not on this desk.</p></div>`);
  const d = await rq.json() as { request?: Request; events?: Ev[]; suggested?: { price: number | null; why: string };
    inspector?: InspectorView; links?: { buyer: string; seller: string };
    calls?: Call[]; texts?: Text[]; working?: { id: string; name: string } | null; me?: { id: string; name: string } };
  if (d.inspector) return void inspectorJob(d.inspector);
  const q = d.request!;
  const board = rb.ok ? await rb.json() as Board : null;
  const role = board?.me.role ?? "viewer";
  /* Inspectors in the car's state first, then the rest by state, then the
     desk's own people, who can still take one themselves. */
  const carState = (q.seller_state ?? "").toUpperCase();
  const rank = (p: Person) => p.role !== "technician" ? 2 : carState && (p.state ?? "").toUpperCase() === carState ? 0 : 1;
  const inspectors = (board?.people ?? []).filter((p) => p.role === "technician" || p.role === "advisor" || p.role === "admin")
    .sort((x, y) => rank(x) - rank(y) || (x.state ?? "").localeCompare(y.state ?? "") || x.full_name.localeCompare(y.full_name));
  const idx = STEPS.indexOf(q.step as typeof STEPS[number]);

  /* Eight steps as pills wrapped to three lines on a phone. A bar of
     eight segments and one line of words says the same in one row. */
  const steps = `<div class="ppi-bar" aria-hidden="true">${STEPS.map((s, i) =>
    `<i class="${s === q.step ? "on" : idx > i ? "done" : ""}"></i>`).join("")}</div>
    <p class="ppi-where"><b>${esc(STEP_LABEL[q.step as Step] ?? q.step)}</b>${idx >= 0
      ? ` · step ${idx + 1} of ${STEPS.length}${STEPS[idx + 1] ? ` · next ${esc(STEP_LABEL[STEPS[idx + 1]!])}` : ""}` : ""}</p>`;

  const actions: string[] = [];
  if (q.step === "new" && !q.price_approved_at && seesEverything(role)) {
    actions.push(`<div class="field"><label>Price to send</label>
      <input id="ppi-price" inputmode="decimal" value="${esc(d.suggested?.price ?? "")}" placeholder="Set the price">
      <p class="quiet">${esc(d.suggested?.why ?? "")}</p></div>
      <button class="btn primary" data-do="price">Approve price and text the booking link</button>`);
  }
  if (!["closed", "refunded", "lost"].includes(q.step) && seesEverything(role)) {
    const bad = !q.vehicle_year || !q.vehicle_make || !q.vehicle_model;
    actions.push(`<details ${bad ? "open" : ""}><summary>Buyer and vehicle</summary>
      <div class="field"><label>Buyer name</label><input id="b-name" value="${esc(q.buyer_name ?? "")}"></div>
      <div class="field"><label>Buyer phone</label><input id="b-phone" inputmode="tel" value="${esc(q.buyer_phone ?? "")}"></div>
      <div class="field"><label>Buyer email</label><input id="b-email" type="email" value="${esc(q.buyer_email ?? "")}"></div>
      <div class="field"><label>Year</label><input id="v-year" inputmode="numeric" maxlength="4" value="${esc(q.vehicle_year ?? "")}"></div>
      <div class="field"><label>Make</label><input id="v-make" value="${esc(q.vehicle_make ?? "")}"></div>
      <div class="field"><label>Model</label><input id="v-model" value="${esc(q.vehicle_model ?? "")}"></div>
      <div class="field"><label>Type</label><input id="v-type" value="${esc(q.vehicle_type ?? "")}" placeholder="car, truck, RV, diesel"></div>
      <div class="field"><label>VIN</label><input id="v-vin" maxlength="17" value="${esc(q.vin ?? "")}"></div>
      <button class="btn" data-do="buyer">Save buyer and vehicle</button></details>`);
  }
  if (["new", "signed_paid", "scheduling", "scheduled", "missed"].includes(q.step) && seesEverything(role)) {
    actions.push(`<details ${q.seller_phone ? "" : "open"}><summary>Seller details</summary>
      <div class="field"><label>Seller is</label><select id="s-kind">
        ${["unknown", "dealer", "private"].map((k) => `<option ${q.seller_kind === k ? "selected" : ""}>${k}</option>`).join("")}</select></div>
      <div class="field"><label>Dealer name</label><input id="s-dealer" value="${esc(q.dealer_name ?? "")}"></div>
      <div class="field"><label>Seller name</label><input id="s-name" value="${esc(q.seller_name ?? "")}"></div>
      <div class="field"><label>Seller phone</label><input id="s-phone" value="${esc(q.seller_phone ?? "")}"></div>
      <div class="field"><label>Address</label><input id="s-addr" value="${esc(q.seller_address ?? "")}"></div>
      <div class="field"><label>City</label><input id="s-city" value="${esc(q.seller_city ?? "")}"></div>
      <div class="field"><label>State</label><input id="s-state" maxlength="2" value="${esc(q.seller_state ?? "")}"></div>
      <button class="btn" data-do="seller">Save seller</button></details>`);
  }
  if (["scheduling", "scheduled", "missed"].includes(q.step) && seesEverything(role)) {
    actions.push(`<details open><summary>${q.step === "scheduling" ? "Schedule it" : "Change the time or inspector"}</summary>
      <div class="field"><label>Day and time</label><input id="sch-at" type="datetime-local"></div>
      <div class="field"><label>Inspector</label><select id="sch-insp"><option value="">Pick one</option>
        ${inspectors.map((p) => `<option value="${esc(p.id)}" ${q.inspector_id === p.id ? "selected" : ""}>${esc(p.full_name)} · ${esc(p.market ?? ROLE_WORD[p.role] ?? p.role)}</option>`).join("")}
      </select></div>
      <button class="btn primary" data-do="schedule">Save and text buyer, seller and inspector</button></details>`);
  }
  const moves = [...STEPS, "lost", "missed", "refunded"].filter((to) => mayMove(role, q.step, to as Step).ok && to !== "scheduled");
  if (moves.length) {
    actions.push(`<div class="ppi-actions">${moves.map((to) =>
      `<button class="btn ${to === "lost" || to === "missed" ? "ghost" : ""}" data-move="${esc(to)}">Move to ${esc(STEP_LABEL[to as Step])}</button>`).join("")}</div>`);
  }
  if (!mayApproveReport(role) && q.step === "review") actions.push(`<p class="quiet">An admin approves the report.</p>`);
  if (["inspecting", "review", "delivered", "closed"].includes(q.step)) {
    actions.unshift(`<a class="btn ${q.step === "review" ? "primary" : ""}" href="#/ppi-report/${esc(q.id)}">${q.step === "review" ? "Read the report before approving" : "Open the report"}</a>`);
  }

  const evs = (d.events ?? []).slice().reverse().map((e) => `<div class="ppi-ev"><div class="ppi-ev-top">
    ${e.kind === "step" ? `<b>${esc(STEP_LABEL[e.step_to as Step] ?? e.step_to)}</b>` : `<b>${esc(e.kind === "text" ? `Text${e.audience ? ` to ${e.audience}` : ""}` : e.kind === "note" ? "Note" : e.kind)}</b>`}
    <span class="quiet">${esc(at(e.at))}${e.actor_name ? ` · ${esc(e.actor_name)}` : ""}</span></div>
    ${e.body ? `<p>${esc(e.body)}</p>` : ""}</div>`).join("");

  const desk = seesEverything(role);
  mount(`<div class="page ppi">
    <header class="page-head"><a class="back" href="#/">&larr; Board</a>
      <h1>${esc(ppiCode(q.code))} · ${esc(vehicleOf(q))}</h1>${steps}</header>
    ${desk ? workingBar(d.working ?? null, d.me?.id ?? "") : ""}
    <div class="card"><div class="card-head">Details</div><div class="pad">
      <div class="kv"><span>Buyer</span><b>${esc(q.buyer_name ?? "")} · ${tel(q.buyer_phone)} ${q.buyer_email ? `· ${esc(q.buyer_email)}` : ""}</b></div>
      <div class="kv"><span>Seller</span><b>${esc(q.seller_kind === "dealer" ? (q.dealer_name ?? "Dealer") : (q.seller_name ?? "Not yet"))} · ${tel(q.seller_phone)}</b></div>
      <div class="kv"><span>Where</span><b>${esc([q.seller_address, q.seller_city, q.seller_state].filter(Boolean).join(", ") || "Not yet")}</b></div>
      <div class="kv"><span>Vehicle</span><b>${esc(q.vehicle_type ?? "")} ${q.vin ? `· VIN ${esc(q.vin)}` : ""} ${q.plate ? `· ${esc(q.plate)} ${esc(q.plate_state ?? "")}` : ""}</b></div>
      <div class="kv"><span>Price</span><b>${q.price != null ? money(q.price) : "Not set"} ${q.paid_at ? "· paid" : ""}</b></div>
      <div class="kv"><span>When</span><b>${q.scheduled_for ? esc(at(q.scheduled_for)) : "Not scheduled"}</b></div>
      <div class="kv"><span>From</span><b>${esc(q.source ?? "")}${q.from_brand ? ` · handed off by ${esc(q.from_brand)}` : ""}</b></div>
      ${q.notes ? `<p class="ppi-notes">${esc(q.notes)}</p>` : ""}
      ${d.links ? `<div class="ppi-actions">
        <button class="btn sm" data-copy="${esc(d.links.buyer)}">Copy buyer link</button>
        <button class="btn ghost sm" data-copy="${esc(d.links.seller)}">Copy seller link</button></div>` : ""}
    </div></div>
    ${desk ? textsCard(d.texts ?? [], q) : ""}
    ${desk ? callsCard(d.calls ?? []) : ""}
    ${actions.length ? `<div class="card"><div class="card-head">Next</div><div class="pad ppi-next">${actions.join("")}</div></div>` : ""}
    <p id="ppi-msg" class="msg"></p>
    <div class="card"><div class="card-head">History</div><div class="pad">
      <div class="field"><textarea id="ppi-note" rows="2" placeholder="Add a note"></textarea></div>
      <button class="btn" data-do="note">Add note</button>
      ${evs || `<p class="quiet">Nothing yet.</p>`}</div></div></div>`);

  const say = (t: string, bad = false) => {
    const m = document.getElementById("ppi-msg");
    if (m) { m.textContent = t; m.className = `msg${bad ? " bad" : ""}`; }
  };
  const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";
  const post = async (path: string, body: unknown) => {
    const res = await authed(`/api/ppidesk/request/${encodeURIComponent(q.id)}/${path}`, body);
    const j = await res.json().catch(() => ({})) as { error?: string; texts?: Record<string, { ok: boolean; why?: string }> };
    if (!res.ok) return say(j.error ?? "That did not save.", true);
    const notSent = Object.entries(j.texts ?? {}).filter(([, t]) => !t.ok);
    const held = notSent.filter(([, t]) => /^held/.test(t.why ?? ""));
    const failed = notSent.filter(([, t]) => !/^held/.test(t.why ?? "")).map(([k, t]) => `${k}: ${t.why}`);
    /* Texts only go 9am to 8pm Pacific. A held one is not a failure, and the
       old 2.5 second flash of "Not texted, B1: outside the send window" was
       gone before anyone on the night desk read it (2026-10-17). */
    say(failed.length ? `Saved. Not texted, ${failed.join(". ")}`
      : held.length ? "Saved. The text was held; the desk sends it by itself when texting opens." : path === "text" ? "Sent." : "Saved.",
      failed.length > 0);
    setTimeout(() => void ppiRequestPage(q.id), failed.length || held.length ? 6000 : 600);
  };

  /* Texts are off on the PPI desk for now (ppi_desk_texts), so staff send
     the booking and tracker pages by hand from their own phone. */
  document.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((btn) => btn.addEventListener("click", async () => {
    const link = btn.dataset.copy ?? "";
    try { await navigator.clipboard.writeText(link); say("Link copied."); }
    catch { window.prompt("Copy this link", link); }
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-do]").forEach((btn) => btn.addEventListener("click", () => {
    btn.disabled = true;
    const what = btn.dataset.do;
    if (what === "price") void post("price", { price: val("ppi-price") });
    if (what === "buyer") void post("buyer", { buyer_name: val("b-name"), buyer_phone: val("b-phone"), buyer_email: val("b-email"),
      vehicle_year: val("v-year"), vehicle_make: val("v-make"), vehicle_model: val("v-model"), vehicle_type: val("v-type"), vin: val("v-vin") });
    if (what === "seller") void post("seller", { seller_kind: val("s-kind"), dealer_name: val("s-dealer"), seller_name: val("s-name"),
      seller_phone: val("s-phone"), seller_address: val("s-addr"), seller_city: val("s-city"), seller_state: val("s-state") });
    if (what === "schedule") void post("schedule", {
      scheduled_for: val("sch-at") ? new Date(val("sch-at")).toISOString() : "", inspector_id: val("sch-insp") });
    if (what === "note") void post("note", { note: val("ppi-note") });
    if (what === "text") {
      if (!val("ppi-text").trim()) { btn.disabled = false; return say("Write the text first.", true); }
      void post("text", { text: val("ppi-text") });
    }
  }));
  wireCallButtons();
  wireWorking((b) => authed(`/api/ppidesk/request/${encodeURIComponent(q.id)}/claim`, b), say, () => void ppiRequestPage(q.id));
  const thread = document.querySelector(".ppi-thread");
  if (thread) thread.scrollTop = thread.scrollHeight;
  document.querySelectorAll<HTMLButtonElement>("[data-move]").forEach((btn) => btn.addEventListener("click", () => {
    // Sending a report back needs the reason: it goes to the inspector.
    let note: string | null = null;
    if (q.step === "review" && btn.dataset.move === "inspecting") {
      note = window.prompt("What does the inspector need to fix?");
      if (!note || !note.trim()) return;
    }
    btn.disabled = true;
    void post("move", { to: btn.dataset.move, ...(note ? { note } : {}) });
  }));
}

/* ─────────────── calls, texts, and who is working it ─────────────── */

type Call = { at: string; subject: string | null; seconds: number; recording: string | null; summary: string | null;
  transcript: string | null; line: string | null; callrail_id: string | null };
type Text = { at: string; way: "in" | "out"; body: string; by: string | null; status: string | null };

const mins = (s: number) => s >= 60 ? `${Math.floor(s / 60)} min ${s % 60 ? `${s % 60} s` : ""}`.trim() : `${s} s`;

/** Every call from the buyer's number, with the recording and the words. */
function callsCard(calls: Call[]): string {
  if (!calls.length) return "";
  return `<div class="card"><div class="card-head"><span>Calls</span><span class="ppi-count">${calls.length}</span></div><div class="pad">
    ${calls.map((c) => `<div class="ppi-call">
      <div class="ppi-ev-top"><b>${esc(c.subject ?? "Call")}${c.seconds ? ` · ${esc(mins(c.seconds))}` : ""}</b>
        <span class="quiet">${esc(at(c.at))}</span></div>
      ${c.summary ? `<p>${esc(c.summary)}</p>` : ""}
      ${c.callrail_id ? callButtonsHtml(c.callrail_id, { read: !c.transcript })
        : c.recording ? `<a class="linkbtn" href="${esc(c.recording)}" target="_blank" rel="noopener">Play the recording</a>` : ""}
      ${c.transcript ? `<details class="ppi-tx"><summary>Read the call</summary><pre>${esc(c.transcript)}</pre></details>` : ""}
    </div>`).join("")}</div></div>`;
}

/**
 * THE TEXT THREAD WITH THE BUYER, and a box to answer them. It goes from
 * the PPI line, 951-363-2777, the number their replies come back to.
 */
export function textsCard(texts: Text[], q: { buyer_phone: string | null; buyer_name: string | null }, id = "ppi-text"): string {
  const bubbles = texts.map((t) => `<div class="ppi-bub ${t.way}">
      <p>${esc(t.body)}</p>
      <small>${t.way === "in" ? esc(q.buyer_name || "Buyer") : esc(t.by ?? "PPI desk")} · ${esc(at(t.at))}${
        t.way === "out" && t.status && t.status !== "delivered" ? ` · ${esc(t.status)}` : ""}</small></div>`).join("");
  return `<div class="card"><div class="card-head"><span>Texts with the buyer</span><span class="quiet">from (951) 363-2777</span></div><div class="pad">
    ${texts.length ? `<div class="ppi-thread">${bubbles}</div>` : `<p class="quiet">No texts yet.</p>`}
    ${q.buyer_phone ? `<div class="field"><textarea id="${id}" rows="3" maxlength="900" placeholder="Text ${esc(q.buyer_name || "the buyer")}"></textarea></div>
      <button class="btn primary" data-do="text">Send text</button>` : `<p class="quiet">No number to text.</p>`}
  </div></div>`;
}

/** "Nobody is working this" with a button, or who is, with Take over / Let it go. */
export function workingBar(w: { id: string; name: string } | null, meId: string): string {
  const mine = w && w.id === meId;
  return `<div class="card ppi-working${w ? (mine ? " mine" : " taken") : " free"}"><div class="pad">
    <b>${w ? (mine ? "You are working this" : `${esc(w.name)} is working this`) : "Nobody is working this yet"}</b>
    ${w ? (mine ? `<button class="btn ghost sm" data-claim="release">Let it go</button>`
      : `<button class="btn ghost sm" data-claim="take">Take it over</button>`)
      : `<button class="btn primary sm" data-claim="claim">I'm working this</button>`}
  </div></div>`;
}

export function wireWorking(send: (b: Record<string, unknown>) => Promise<Response>, say: (t: string, bad?: boolean) => void,
  again: () => void): void {
  document.querySelectorAll<HTMLButtonElement>("[data-claim]").forEach((btn) => btn.addEventListener("click", async () => {
    const how = btn.dataset.claim;
    if (how === "take" && !window.confirm("Take this over? The person working it will see you took it.")) return;
    btn.disabled = true;
    const res = await send(how === "release" ? { release: true } : how === "take" ? { take: true } : {});
    const j = await res.json().catch(() => ({})) as { error?: string };
    if (!res.ok) { btn.disabled = false; return say(j.error ?? "That did not save.", true); }
    again();
  }));
}

/* ───────────────────────────── new, by phone ───────────────────────────── */

export function ppiNewPage(): void {
  const f = (id: string, label: string, extra = "") =>
    `<div class="field"><label for="${id}">${label}</label><input id="${id}" ${extra}></div>`;
  mount(`<div class="page ppi">
    <header class="page-head"><a class="back" href="#/">&larr; Board</a><h1>New PPI Request</h1>
      <p>Somebody on the phone. Their name and number are enough to start.</p></header>
    <div class="card"><div class="card-head">The buyer</div><div class="pad">
    ${f("n-name", "Buyer name", 'autocomplete="off"')}${f("n-phone", "Buyer phone", 'inputmode="tel"')}${f("n-email", "Buyer email", 'type="email"')}
    </div></div><div class="card"><div class="card-head">The vehicle</div><div class="pad">
    <div class="ppi-row3">${f("n-year", "Year", 'inputmode="numeric" maxlength="4"')}${f("n-make", "Make")}${f("n-model", "Model")}</div>
    ${f("n-type", "Vehicle type", 'placeholder="Car, pickup, RV, diesel, motorcycle…"')}${f("n-vin", "VIN", 'maxlength="17"')}
    <div class="ppi-row2">${f("n-city", "City the car is in")}${f("n-state", "State", 'maxlength="2" autocapitalize="characters"')}</div>
    <div class="field"><label for="n-notes">Notes</label><textarea id="n-notes" rows="3"></textarea></div>
    <p id="n-msg" class="msg"></p>
    <button class="btn primary wide ppi-full" id="n-go">Create request</button></div></div></div>`);
  const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value;
  document.getElementById("n-go")?.addEventListener("click", async (e) => {
    (e.target as HTMLButtonElement).disabled = true;
    const res = await authed("/api/ppidesk/new", {
      buyer_name: v("n-name"), buyer_phone: v("n-phone"), buyer_email: v("n-email"), vehicle_year: v("n-year"),
      vehicle_make: v("n-make"), vehicle_model: v("n-model"), vehicle_type: v("n-type"), vin: v("n-vin"),
      seller_city: v("n-city"), seller_state: v("n-state"), notes: v("n-notes"),
    });
    const j = await res.json().catch(() => ({})) as { id?: string; error?: string };
    if (res.ok && j.id) location.hash = `#/ppi/${j.id}`;
    else { (e.target as HTMLButtonElement).disabled = false;
      const m = document.getElementById("n-msg"); if (m) { m.textContent = j.error ?? "Did not save."; m.className = "msg bad"; } }
  });
}

/* ───────────────────────────── the inspector ───────────────────────────── */

/**
 * WHAT AN INSPECTOR SEES. Their jobs, the seller, and their contact at
 * Supercanic. The server builds this from a list of allowed fields
 * (inspectorView), so there is nothing here to forget to hide.
 */
export async function ppiMine(): Promise<void> {
  mount(`<div class="card"><p class="quiet">Loading your jobs…</p></div>`);
  const r = await authed("/api/ppidesk/mine");
  if (!r.ok) return void mount(`<div class="card"><p class="msg bad">Your jobs did not load.</p></div>`);
  const { jobs } = await r.json() as { jobs: InspectorView[] };
  mount(`<div class="page ppi">
    <header class="page-head"><h1>My Inspections</h1><p>Tap one for the address, the seller and the report.</p></header>
    ${jobs.length ? `<div class="ppi-list">${jobs.map((j) => `<a class="ppi-card" href="#/ppi/${esc(j.id)}"><b>${esc(j.code)} · ${esc(j.vehicle)}</b>
      <small>${esc(at(j.scheduled_for))} · ${esc([j.seller_city, j.seller_state].filter(Boolean).join(", "))}</small>
      <small>${esc(STEP_LABEL[j.step] ?? j.step)}</small></a>`).join("")}</div>`
      : `<div class="card"><p class="empty">No inspections assigned to you right now.</p></div>`}
    <div class="card"><p class="quiet">Want to know who you're working with? Meet the founder, see how we run inspections and what else we build for mechanics.</p>
      <p><a class="btn" href="https://mobilemechanics.app/" target="_blank" rel="noopener">Learn more at mobilemechanics.app &rarr;</a></p></div></div>`);
}

function inspectorJob(j: InspectorView): void {
  mount(`<div class="page ppi">
    <header class="page-head"><a class="back" href="#/ppi-mine">&larr; My Inspections</a>
      <h1>${esc(j.code)} · ${esc(j.vehicle)}</h1></header>
    <div class="card"><div class="card-head">The job</div><div class="pad">
    <div class="kv"><span>When</span><b>${esc(at(j.scheduled_for))}</b></div>
    <div class="kv"><span>Seller</span><b>${esc(j.dealer_name ?? j.seller_name ?? "")} · ${tel(j.seller_phone)}</b></div>
    <div class="kv"><span>Address</span><b>${esc([j.seller_address, j.seller_city, j.seller_state].filter(Boolean).join(", "))}</b></div>
    <div class="kv"><span>Vehicle</span><b>${esc(j.vehicle_type ?? "")}${j.vin_last4 ? ` · VIN ends ${esc(j.vin_last4)}` : ""}</b></div>
    ${j.listing_url ? `<div class="kv"><span>Listing</span><b><a href="${esc(j.listing_url)}" target="_blank" rel="noopener">Open</a></b></div>` : ""}
    <div class="kv"><span>Your contact at Supercanic</span><b>${esc(j.contact?.name ?? "The desk")} · ${tel(j.contact?.phone ?? null)}</b></div>
    <p id="ij-msg" class="msg"></p>
    ${j.step === "scheduled" ? `<button class="btn primary wide ppi-full" id="ij-start">I'm here. Start the inspection</button>` : ""}
    ${j.step === "inspecting" || j.step === "review" ? `<a class="btn primary wide ppi-full" href="#/ppi-report/${esc(j.id)}">${j.step === "review" ? "Report sent for review" : "Fill in the report"}</a>` : ""}
  </div></div></div>`);
  document.getElementById("ij-start")?.addEventListener("click", async (e) => {
    (e.target as HTMLButtonElement).disabled = true;
    const r = await authed(`/api/ppidesk/request/${encodeURIComponent(j.id)}/start`, {});
    const out = await r.json().catch(() => ({})) as { error?: string };
    if (r.ok) location.hash = `#/ppi-report/${j.id}`;
    else { const m = document.getElementById("ij-msg"); if (m) { m.textContent = out.error ?? "Did not start."; m.className = "msg bad"; } }
  });
}

/* ───────────────────────────── the report ───────────────────────────── */

type RPoint = { key: string; name: string };
type RSection = { id: string; title: string; points: RPoint[] };
type RQuestion = { key: string; text: string; options: string[]; explainOn?: string[] };
type RMark = { v: string; note?: string };
type RState = {
  code: string; vehicle: string; step: Step; sections: RSection[]; verdicts: Array<{ key: string; short: string; label: string }>;
  questions: RQuestion[]; editable: boolean; photo_folder: string; photo_links: Record<string, string>;
  report: { marks: Record<string, RMark>; answers: Record<string, { a: string; detail?: string }>; summary: string | null;
    photos: string[]; odometer: string | null; submitted_at: string | null; sent_back_note: string | null } | null;
  /* Only on a shop's own job (server/routes/ownppi.ts). */
  seller_kind?: string; all_questions?: Array<RQuestion & { dealerOnly?: boolean }>; share_link?: string | null; customer?: string | null;
};

/**
 * THE INSPECTOR'S REPORT: the 122 points, the seller questions, photos,
 * Save and Submit. The same screen read-only for the desk. Marks are kept
 * in this page as they are tapped and saved to the server on Save, so a
 * lost signal costs a tap of Save, not the inspection.
 */
export async function ppiReportPage(id: string, onJob = false): Promise<void> {
  /* ON A SHOP'S OWN JOB the same screen saves to the job, has no desk
     review, and finishing hands back the customer's link (Anthony,
     2026-10-29: Brian Huey could not fill it out on his own PPI lead). */
  const path = onJob ? `/api/jobs/${encodeURIComponent(id)}/ppi-report` : `/api/ppidesk/request/${encodeURIComponent(id)}/report`;
  const back = onJob ? `#/job/${esc(id)}` : `#/ppi/${esc(id)}`;
  mount(`<div class="card"><p class="quiet">Loading the report…</p></div>`);
  const r = await authed(path);
  if (!r.ok) return void mount(`<div class="card"><p class="msg bad">That report is not yours to open.</p></div>`);
  const st = await r.json() as RState;
  const marks: Record<string, RMark> = { ...(st.report?.marks ?? {}) };
  const answers: Record<string, { a: string; detail?: string }> = { ...(st.report?.answers ?? {}) };
  const photos: string[] = [...(st.report?.photos ?? [])];
  const links: Record<string, string> = { ...st.photo_links };
  const ro = !st.editable;
  /* THE PHONE KEEPS A COPY until the server has it. 122 taps live only in
     this page until Save, and an inspector under a truck loses signal, gets
     a call, or the browser reloads the tab. The draft is written on every
     change and dropped after a save the server accepted (2026-10-15). */
  const draftKey = `ppi-draft-${onJob ? "job-" : ""}${id}`;
  let kind = st.seller_kind ?? "unknown";
  let shareLink = st.share_link ?? null;
  const askFor = () => { if (onJob && st.all_questions) st.questions = st.all_questions.filter((q) => !q.dealerOnly || kind === "dealer"); };
  askFor();
  let restored = false;
  if (!ro) {
    try {
      const d = JSON.parse(localStorage.getItem(draftKey) ?? "null") as
        { marks?: Record<string, RMark>; answers?: Record<string, { a: string; detail?: string }>; photos?: string[];
          odometer?: string; summary?: string } | null;
      if (d) {
        Object.assign(marks, d.marks ?? {}); Object.assign(answers, d.answers ?? {});
        for (const p of d.photos ?? []) if (!photos.includes(p) && p.startsWith(`${st.photo_folder}/`)) photos.push(p);
        if (st.report) { st.report.odometer = d.odometer ?? st.report.odometer; st.report.summary = d.summary ?? st.report.summary; }
        else if (d.odometer || d.summary) st.report = { marks: {}, answers: {}, summary: d.summary ?? null, photos: [], odometer: d.odometer ?? null, submitted_at: null, sent_back_note: null };
        restored = true;
      }
    } catch { /* private mode or a broken draft: the server copy stands */ }
  }
  let note = restored ? { t: "Your unsaved marks from this phone are back. Tap Save to keep them.", bad: false } : { t: "", bad: false };
  /* EVERY POINT STARTS AT OK on a report nobody has marked yet, so the
     mechanic taps only what is wrong, the way the shop inspection sheet
     works (Anthony, 2026-10-29: "Preload all set to ok"). A report with
     any marks, saved or on this phone, is left exactly as it was. */
  if (!ro && !Object.keys(marks).length) {
    for (const sec of st.sections) for (const p of sec.points) marks[p.key] = { v: "good" };
  }
  const total = st.sections.reduce((n, x) => n + x.points.length, 0);
  const done = () => Object.keys(marks).length;

  const native = hasCamera();
  const draw = () => {
    const point = (p: RPoint) => {
      const m = marks[p.key];
      const needNote = m && (m.v === "watch" || m.v === "now");
      return `<div class="ppi-pt"><div class="ppi-pt-name">${esc(p.name)}</div>
        <div class="ppi-vs">${st.verdicts.map((v) => `<button class="ppi-v ppi-v-${esc(v.key)}${m?.v === v.key ? " on" : ""}"
          data-pt="${esc(p.key)}" data-v="${esc(v.key)}" ${ro ? "disabled" : ""} title="${esc(v.label)}">${esc(v.short)}</button>`).join("")}</div>
        ${needNote || m?.note ? `<input class="ppi-note" data-note="${esc(p.key)}" placeholder="What did you find?" value="${esc(m?.note ?? "")}" ${ro ? "disabled" : ""}>` : ""}
      </div>`;
    };
    mount(`<div class="page ppi">
      <header class="page-head"><a class="back" href="${back}">&larr; Back</a>
        <h1>${esc(st.code)} · ${esc(st.vehicle)}</h1>
        <p>${done()} of ${total} points marked.${ro ? " Read only." : " Every point starts at OK. Change the ones that are not. Worth doing and Needs doing need a note."}</p>
        <div class="ppi-bar one" aria-hidden="true"><i class="done" style="width:${total ? Math.round(done() / total * 100) : 0}%"></i></div></header>
      ${st.report?.sent_back_note ? `<p class="msg bad">Sent back: ${esc(st.report.sent_back_note)}</p>` : ""}
      <div class="card"><div class="pad"><div class="field ppi-odo"><label for="rp-odo">Odometer</label><input id="rp-odo" inputmode="numeric" value="${esc(st.report?.odometer ?? "")}" ${ro ? "disabled" : ""}></div></div></div>
      ${st.sections.map((sec) => `<details class="card ppi-sec" ${ro ? "" : "open"}><summary><b>${esc(sec.title)}</b>
        <span class="ppi-count">${sec.points.filter((p) => marks[p.key]).length}/${sec.points.length}</span></summary>
        <div class="ppi-pts">${sec.points.map(point).join("")}</div></details>`).join("")}
      <div class="card"><div class="card-head">About the Seller</div><div class="pad">
        <p class="quiet">${onJob ? "For your own records. Your customer never sees these." : "For the desk only. The buyer never sees these."}</p>
        ${onJob ? `<div class="field"><label for="rp-kind">Who is selling it?</label><select id="rp-kind" ${ro ? "disabled" : ""}>
          ${[["unknown", "Pick one"], ["dealer", "A dealer"], ["private", "A private seller"]].map(([k, l]) =>
            `<option value="${k}" ${kind === k ? "selected" : ""}>${l}</option>`).join("")}</select></div>` : ""}
        ${st.questions.map((q) => `<div class="field"><label>${esc(q.text)}</label>
          <select data-q="${esc(q.key)}" ${ro ? "disabled" : ""}><option value="">Pick one</option>
          ${q.options.map((o) => `<option ${answers[q.key]?.a === o ? "selected" : ""}>${esc(o)}</option>`).join("")}</select>
          ${q.explainOn ? `<input class="ppi-qd" data-qd="${esc(q.key)}" placeholder="Describe it" value="${esc(answers[q.key]?.detail ?? "")}" ${ro ? "disabled" : ""}>` : ""}</div>`).join("")}
      </div></div>
      <div class="card"><div class="card-head">Photos and Summary</div><div class="pad">
        <div class="ppi-photos">${photos.map((p, i) => `<figure class="ppi-ph">${links[p] ? `<img src="${esc(links[p])}" alt="Inspection photo ${i + 1}">` : `<span class="pill">Photo saved</span>`}
          ${ro ? "" : `<button class="ppi-ph-x" data-rm="${i}" aria-label="Remove photo ${i + 1}">&times;</button>`}</figure>`).join("")}</div>
        ${ro ? "" : `<div class="ppi-ph-add">
          ${native ? `<button class="btn primary" id="rp-cam">Take photo</button><button class="btn" id="rp-pick">Choose photos</button>`
            : `<label class="btn primary">Take photo<input id="rp-cam-in" class="ppi-file" type="file" accept="image/*" capture="environment"></label>
               <label class="btn">Choose photos<input id="rp-files" class="ppi-file" type="file" accept="image/*" multiple></label>`}
          <span class="quiet">${photos.length} photo${photos.length === 1 ? "" : "s"}</span></div>`}
        <div class="field"><label>Summary for the buyer</label><textarea id="rp-sum" rows="4" ${ro ? "disabled" : ""}>${esc(st.report?.summary ?? "")}</textarea></div>
        <p id="rp-msg" class="msg${note.bad ? " bad" : ""}" role="status">${esc(note.t)}</p>
        ${onJob && shareLink ? `<div class="field"><label for="rp-link">Your customer's report${st.customer ? ` for ${esc(st.customer)}` : ""}</label>
          <input id="rp-link" readonly value="${esc(shareLink)}">
          <div class="ppi-ph-add"><button class="btn primary" id="rp-copy">Copy link</button>
          <a class="btn" href="sms:?&body=${encodeURIComponent(`Your pre-purchase inspection report: ${shareLink}`)}">Text it</a>
          <a class="btn" href="${esc(shareLink)}" target="_blank" rel="noopener">Open it</a></div></div>` : ""}
      </div></div>
      ${ro ? "" : `<div class="ppi-savebar"><button class="btn" id="rp-save">Save</button>
        <button class="btn primary" id="rp-submit">${onJob ? (shareLink ? "Finish again and update the link" : "Finish and get the customer link") : "Submit for review"}</button></div>`}</div>`);
    wire();
  };

  const collect = () => {
    /* The odometer and summary too: every mark tap redraws the page from
       st.report, and before 2026-10-15 that threw away a summary typed but
       not yet saved. */
    const odo = document.getElementById("rp-odo") as HTMLInputElement | null;
    const sum = document.getElementById("rp-sum") as HTMLTextAreaElement | null;
    if (odo && sum && !ro) {
      st.report = { ...(st.report ?? { marks: {}, answers: {}, photos: [], submitted_at: null, sent_back_note: null }),
        odometer: odo.value, summary: sum.value };
    }
    document.querySelectorAll<HTMLInputElement>("[data-note]").forEach((el) => {
      const k = el.dataset.note!; if (marks[k]) marks[k] = { ...marks[k]!, note: el.value };
    });
    document.querySelectorAll<HTMLSelectElement>("[data-q]").forEach((el) => {
      const k = el.dataset.q!; const d = (document.querySelector<HTMLInputElement>(`[data-qd="${k}"]`))?.value ?? "";
      if (el.value) answers[k] = { a: el.value, ...(d ? { detail: d } : {}) }; else delete answers[k];
    });
  };
  const say = (t: string, bad = false) => { note = { t, bad }; const m = document.getElementById("rp-msg"); if (m) { m.textContent = t; m.className = `msg${bad ? " bad" : ""}`; } };
  const keepDraft = () => {
    if (ro) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ marks, answers, photos,
        odometer: (document.getElementById("rp-odo") as HTMLInputElement | null)?.value ?? st.report?.odometer ?? "",
        summary: (document.getElementById("rp-sum") as HTMLTextAreaElement | null)?.value ?? st.report?.summary ?? "" }));
    } catch { /* storage full or blocked: Save still works */ }
  };
  const dropDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* nothing to drop */ } };
  const save = async (submit: boolean) => {
    collect();
    const res = await authed(path, {
      marks, answers, photos, submit, ...(onJob ? { seller_kind: kind } : {}),
      odometer: (document.getElementById("rp-odo") as HTMLInputElement | null)?.value ?? "",
      summary: (document.getElementById("rp-sum") as HTMLTextAreaElement | null)?.value ?? "",
    });
    const out = await res.json().catch(() => ({})) as { error?: string; share_link?: string | null };
    if (!res.ok) { keepDraft(); say(out.error ?? "That did not save. Your marks are kept on this phone. Try again.", true); return false; }
    dropDraft();
    if (submit && onJob) {
      shareLink = out.share_link ?? shareLink;
      const y = window.scrollY; draw(); window.scrollTo(0, y);
      say("Finished. Send your customer the link below.");
      document.getElementById("rp-link")?.scrollIntoView({ block: "center" });
      return true;
    }
    if (submit) { location.hash = `#/ppi/${id}`; return true; }
    say("Saved.");
    return true;
  };

  /* PHOTOS, the way every other photo in this app goes up: shrunk on the
     phone (rotation fixed, about 2048px, JPEG) so a 12MB camera shot is a
     fast upload on one bar of signal, refused in plain words when it is a
     format storage will not take, tried twice before it is called failed.
     The PPI report used to send the raw file, so a HEIC or a 48MP shot
     failed with a storage message about mime types (2026-10-15). */
  const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const addPhotos = async (files: File[]) => {
    if (!files.length) return;
    collect();
    const sb = await db();
    const fails: string[] = [];
    let added = 0;
    for (const [i, f] of files.entries()) {
      say(`Uploading photo ${i + 1} of ${files.length}…`);
      const small = await shrink(f);
      const type = small.type || f.type || "";
      if (!OK_TYPES.includes(type)) {
        fails.push("A photo is in a format the system cannot read. On iPhone: Settings > Camera > Formats > Most Compatible. Or use Take photo.");
        continue;
      }
      if (small.size > 15 * 1024 * 1024) { fails.push("A photo is over 15 MB. Take it again with Take photo."); continue; }
      const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
      const path = `${st.photo_folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      let up = await sb.storage.from("job-photos").upload(path, small, { contentType: type, upsert: false });
      if (up.error) {
        await new Promise((r) => setTimeout(r, 1500));
        up = await sb.storage.from("job-photos").upload(path, small, { contentType: type, upsert: false });
      }
      if (up.error) { fails.push(`Photo ${i + 1} did not upload. Check your signal and add it again.`); continue; }
      photos.push(path);
      links[path] = URL.createObjectURL(small);
      added++;
      keepDraft();
    }
    const saved = added ? await save(false) : true;
    const y = window.scrollY; draw(); window.scrollTo(0, y);
    if (fails.length) say([...new Set(fails)].join(" "), true);
    else if (saved) say(`${added} photo${added === 1 ? "" : "s"} added and saved.`);
  };

  const wire = () => {
    document.querySelectorAll<HTMLButtonElement>("[data-pt]").forEach((b) => b.addEventListener("click", () => {
      collect();
      const k = b.dataset.pt!; marks[k] = { ...(marks[k] ?? {}), v: b.dataset.v! };
      keepDraft();
      const y = window.scrollY; draw(); window.scrollTo(0, y);
    }));
    document.getElementById("rp-save")?.addEventListener("click", () => void save(false));
    document.getElementById("rp-kind")?.addEventListener("change", (e) => {
      collect(); kind = (e.target as HTMLSelectElement).value; askFor(); keepDraft();
      const y = window.scrollY; draw(); window.scrollTo(0, y);
    });
    document.getElementById("rp-copy")?.addEventListener("click", () => {
      const el = document.getElementById("rp-link") as HTMLInputElement | null;
      if (!el) return;
      el.select();
      void navigator.clipboard?.writeText(el.value).then(() => say("Link copied."), () => say("Press and hold the link to copy it."));
    });
    document.getElementById("rp-submit")?.addEventListener("click", () => void save(true));
    const fromInput = (el: HTMLInputElement | null) => el?.addEventListener("change", () => {
      const files = Array.from(el.files ?? []);
      el.value = ""; // so the same photo can be picked again after a failure
      void addPhotos(files);
    });
    fromInput(document.getElementById("rp-files") as HTMLInputElement | null);
    fromInput(document.getElementById("rp-cam-in") as HTMLInputElement | null);
    document.getElementById("rp-cam")?.addEventListener("click", async () => {
      try { const f = await captureNativePhoto(); if (f) await addPhotos([f]); }
      catch (e) { say(`The camera did not open: ${(e as Error).message}. Allow camera access in Settings.`, true); }
    });
    document.getElementById("rp-pick")?.addEventListener("click", async () => {
      try { await addPhotos(await pickNativePhotos(20)); }
      catch (e) { say(`Photos did not open: ${(e as Error).message}. Allow photo access in Settings.`, true); }
    });
    document.querySelectorAll<HTMLButtonElement>("[data-rm]").forEach((b) => b.addEventListener("click", async () => {
      if (!confirm("Remove this photo from the report?")) return;
      collect();
      photos.splice(Number(b.dataset.rm), 1);
      keepDraft();
      await save(false);
      const y = window.scrollY; draw(); window.scrollTo(0, y);
    }));
    document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-note],[data-q],[data-qd],#rp-odo,#rp-sum")
      .forEach((el) => el.addEventListener("input", () => { collect(); keepDraft(); }));
  };
  draw();
}

/* ───────────────────────────── the inspectors ───────────────────────────── */

/**
 * EVERY INSPECTOR, wherever they are (Anthony, 2026-10-15: a login "for the
 * guys all over the country doing them"). Sign-ups from /inspectors wait at
 * the top for the desk to approve or decline; approving texts them a link to
 * set a password. Below, everyone approved, by state, with a button to text
 * a fresh link when the hour-long one ran out. The desk can add somebody
 * directly too. server/routes/ppiteam.ts does the work.
 */
type Insp = { id: string; name: string; phone: string | null; city: string | null; state: string | null;
  market: string | null; notes: string | null; active: boolean; applied_at: string | null; approved_at: string | null;
  invited_at: string | null; profiles: { email: string | null; active: boolean } | null };
type Sent = { ok?: boolean; texted?: boolean; why?: string; link?: string; error?: string; added?: boolean };

export async function ppiInspectors(flash = ""): Promise<void> {
  mount(`<div class="card"><p class="quiet">Loading inspectors…</p></div>`);
  const r = await authed("/api/ppidesk/inspectors");
  if (!r.ok) return void mount(`<div class="card"><p class="msg bad">Only the desk sees the inspector list.</p></div>`);
  const { inspectors } = await r.json() as { inspectors: Insp[] };
  const waiting = inspectors.filter((t) => t.applied_at && !t.approved_at && !t.active);
  const on = inspectors.filter((t) => t.active);
  const off = inspectors.filter((t) => !t.active && !(t.applied_at && !t.approved_at));
  const signup = `${location.origin}/inspectors`;

  const where = (t: Insp) => esc([t.city, t.state].filter(Boolean).join(", ") || t.market || "no city");
  const row = (t: Insp, buttons: string) => `<li class="insp">
      <div><b>${esc(t.name)}</b> <small>${where(t)}</small>
        <small>${tel(t.phone)} · ${esc(t.profiles?.email ?? "no email")}</small>
        ${t.notes ? `<small>${esc(t.notes)}</small>` : ""}
        <small class="quiet">${t.applied_at && !t.approved_at ? `Signed up ${esc(since(t.applied_at))}`
          : t.invited_at ? `Login texted ${esc(since(t.invited_at))}` : "No login texted yet"}</small></div>
      <div class="insp-do">${buttons}</div></li>`;
  const states = [...new Set(on.map((t) => t.state ?? "—"))];

  mount(`<div class="page ppi">
    <header class="page-head ppi-head"><div><h1>Inspectors</h1>
      <p>${on.length} approved${waiting.length ? ` · ${waiting.length} waiting` : ""}. Sign-up page: <a href="${esc(signup)}" target="_blank" rel="noopener">${esc(signup)}</a>
        <button class="btn ghost" data-copy="${esc(signup)}">Copy</button></p></div></header>
    ${flash}
    ${waiting.length ? `<section class="card"><div class="card-head"><span>Waiting for you</span><span class="ppi-count">${waiting.length}</span></div>
      <ul class="insp-list">${waiting.map((t) => row(t,
        `<button class="btn primary" data-act="approve" data-id="${esc(t.id)}">Approve and text login</button>
         <button class="btn ghost" data-act="remove" data-id="${esc(t.id)}" data-ask="Decline ${esc(t.name)}?">Decline</button>`)).join("")}</ul></section>` : ""}
    <section class="card"><div class="card-head"><span>Approved</span><span class="ppi-count">${on.length}</span></div>
      ${on.length ? states.map((st) => `<h3>${esc(st)}</h3><ul class="insp-list">${on.filter((t) => (t.state ?? "—") === st).map((t) => row(t,
        `<button class="btn" data-act="invite" data-id="${esc(t.id)}">Text a login link</button>
         <button class="btn ghost" data-act="remove" data-id="${esc(t.id)}" data-ask="Take ${esc(t.name)} off the desk? They can no longer sign in.">Remove</button>`)).join("")}</ul>`).join("")
        : `<p class="quiet">Nobody yet. Send mechanics the sign-up page, or add one below.</p>`}</section>
    <details class="card"><summary>Add an inspector yourself</summary>
      <div class="field"><label>Full name</label><input id="ai-name" autocomplete="off"></div>
      <div class="field"><label>Cell phone</label><input id="ai-phone" type="tel" inputmode="tel"></div>
      <div class="field"><label>Email (they sign in with this)</label><input id="ai-email" type="email"></div>
      <div class="field"><label>City</label><input id="ai-city"></div>
      <div class="field"><label>State</label><input id="ai-state" maxlength="2" placeholder="CA"></div>
      <div class="field"><label>Notes</label><input id="ai-notes"></div>
      <button class="btn primary" id="ai-go">Add and text their login</button>
      <p class="msg" id="ai-msg"></p></details>
    ${off.length ? `<details class="card"><summary>Removed (${off.length})</summary><ul class="insp-list">${off.map((t) => row(t, "")).join("")}</ul></details>` : ""}
  </div>`);

  /** What happened, said plainly; the link only when a person has to send it. */
  const told = (who: string, d: Sent) => d.error ? `<p class="msg bad">${esc(d.error)}</p>`
    : d.texted ? `<p class="msg ok">Login link texted to ${esc(who)}.</p>`
    : `<div class="card"><p class="msg bad">${esc(d.why ?? "The text did not go.")}</p>
        ${d.link ? `<p>Send ${esc(who)} this link. It works for one hour, for them only.</p>
        <button class="btn" data-copy="${esc(d.link)}">Copy their login link</button>` : ""}</div>`;

  document.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((b) => b.addEventListener("click", async () => {
    try { await navigator.clipboard.writeText(b.dataset.copy!); b.textContent = "Copied"; } catch { prompt("Copy this:", b.dataset.copy!); }
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((b) => b.addEventListener("click", async () => {
    if (b.dataset.ask && !confirm(b.dataset.ask)) return;
    b.disabled = true;
    const t = inspectors.find((x) => x.id === b.dataset.id);
    const res = await authed(`/api/ppidesk/inspectors/${encodeURIComponent(b.dataset.id!)}/${b.dataset.act}`, {});
    const d = await res.json().catch(() => ({})) as Sent;
    if (!res.ok && !d.error) d.error = "That did not work. Try again.";
    void ppiInspectors(b.dataset.act === "remove" ? (d.error ? told(t?.name ?? "them", d) : `<p class="msg ok">${esc(t?.name ?? "They")} can no longer sign in.</p>`)
      : told(t?.name ?? "them", d));
  }));
  document.getElementById("ai-go")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    b.disabled = true;
    const res = await authed("/api/ppidesk/inspectors", { name: val("ai-name"), phone: val("ai-phone"), email: val("ai-email"),
      city: val("ai-city"), state: val("ai-state").toUpperCase(), notes: val("ai-notes") });
    const d = await res.json().catch(() => ({})) as Sent;
    if (!res.ok || !d.added) {
      b.disabled = false;
      const m = document.getElementById("ai-msg")!; m.className = "msg bad"; m.textContent = d.error ?? "That did not save.";
      return;
    }
    void ppiInspectors(told(val("ai-name"), d));
  });
}
